import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

function normalize(v) {
  return (v ?? "").toString().trim();
}

function money(v) {
  if (v === null || v === undefined || v === "") return "$ ******";
  const n = Number(v);
  if (Number.isNaN(n)) return "$ ******";
  return n.toLocaleString("es-MX", { style: "currency", currency: "MXN" });
}

function getEstatus(e) {
  return normalize(e.estatus || e.status || "ACTIVO").toUpperCase();
}

function isActivo(e) {
  return getEstatus(e) === "ACTIVO";
}

function pad2(n) {
  return String(n).padStart(2, "0");
}

function filenameStamp() {
  const d = new Date();
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}_${pad2(
    d.getHours()
  )}${pad2(d.getMinutes())}`;
}

function countBy(items, keyFn) {
  const map = new Map();

  items.forEach((item) => {
    const key = keyFn(item) || "Sin clasificar";
    map.set(key, (map.get(key) || 0) + 1);
  });

  return Array.from(map.entries()).sort((a, b) => b[1] - a[1]);
}

export function exportEmpleadosDirectorioPdf({
  empleados = [],
  resumen = {},
  showSalaries = false,
} = {}) {
  if (!empleados.length) {
    alert("No hay empleados para exportar.");
    return;
  }

  const doc = new jsPDF({ orientation: "landscape", unit: "pt", format: "a4" });
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const marginX = 40;
  const generated = new Date().toLocaleString("es-MX");

  const total = resumen.total ?? empleados.length;
  const activos = resumen.activos ?? empleados.filter(isActivo).length;
  const inactivos = resumen.inactivos ?? total - activos;
  const oficinas = resumen.oficinas ?? countBy(empleados, (e) => normalize(e.oficina)).length;
  const areas = resumen.areas ?? countBy(empleados, (e) => normalize(e.area)).length;
  const totalNomina =
    resumen.totalNomina ?? empleados.reduce((acc, e) => acc + (Number(e.sueldo) || 0), 0);

  const porArea = countBy(empleados, (e) => normalize(e.area));
  const porOficina = countBy(empleados, (e) => normalize(e.oficina));
  const porUbicacion = countBy(empleados, (e) => normalize(e.ubicacion));

  const areaPrincipal = porArea[0] || ["Sin área", 0];
  const oficinaPrincipal = porOficina[0] || ["Sin oficina", 0];

  const header = (title, subtitle = "") => {
    doc.setFillColor(15, 23, 42);
    doc.rect(0, 0, pageWidth, 58, "F");

    doc.setFont("helvetica", "bold");
    doc.setFontSize(16);
    doc.setTextColor(255);
    doc.text(title, marginX, 35);

    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    doc.text(subtitle || `Generado: ${generated}`, pageWidth - marginX, 35, {
      align: "right",
    });

    doc.setTextColor(0);
  };

  const footer = () => {
    const page = doc.internal.getCurrentPageInfo().pageNumber;
    const pageCount = doc.internal.getNumberOfPages();

    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    doc.setTextColor(120);
    doc.text("Documento ejecutivo generado desde el sistema de inventario IP DEPOT.", marginX, pageHeight - 18);
    doc.text(`Página ${page} de ${pageCount}`, pageWidth - marginX, pageHeight - 18, {
      align: "right",
    });
    doc.setTextColor(0);
  };

  const metricCard = (x, y, w, h, label, value, sub = "") => {
    doc.setFillColor(248, 250, 252);
    doc.setDrawColor(226, 232, 240);
    doc.roundedRect(x, y, w, h, 8, 8, "FD");

    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    doc.setTextColor(100);
    doc.text(label, x + 12, y + 17);

    doc.setFont("helvetica", "bold");
    doc.setFontSize(15);
    doc.setTextColor(15, 23, 42);
    doc.text(String(value), x + 12, y + 40);

    if (sub) {
      doc.setFont("helvetica", "normal");
      doc.setFontSize(7.5);
      doc.setTextColor(100);
      doc.text(String(sub), x + 12, y + 56);
    }

    doc.setTextColor(0);
  };

  const sectionTitle = (title, y, subtitle = "") => {
    doc.setFont("helvetica", "bold");
    doc.setFontSize(14);
    doc.setTextColor(15, 23, 42);
    doc.text(title, marginX, y);

    if (subtitle) {
      doc.setFont("helvetica", "normal");
      doc.setFontSize(9);
      doc.setTextColor(100);
      doc.text(subtitle, marginX, y + 15);
    }

    doc.setTextColor(0);
  };

  const barTableRows = (rows) => {
    const max = rows[0]?.[1] || 1;
    return rows.map(([label, value]) => {
      const pct = total > 0 ? ((value / total) * 100).toFixed(1) : "0.0";
      const blocks = Math.max(1, Math.round((value / max) * 18));
      return [label, String(value), `${pct}%`, "█".repeat(blocks)];
    });
  };

  // Página 1
  header("Directorio Ejecutivo de Personal");

  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  doc.setTextColor(70);
  doc.text(
    "Resumen general de personal, cobertura operativa, áreas funcionales y distribución organizacional.",
    marginX,
    82
  );

  const cardY = 110;
  const cardW = 118;
  const cardH = 68;
  const gap = 12;

  metricCard(marginX, cardY, cardW, cardH, "Empleados", total, "Total filtrado");
  metricCard(marginX + (cardW + gap), cardY, cardW, cardH, "Activos", activos, "Personal vigente");
  metricCard(marginX + (cardW + gap) * 2, cardY, cardW, cardH, "Inactivos", inactivos, "Bajas/suspendidos");
  metricCard(marginX + (cardW + gap) * 3, cardY, cardW, cardH, "Oficinas", oficinas, "Cobertura");
  metricCard(marginX + (cardW + gap) * 4, cardY, cardW, cardH, "Áreas", areas, "Estructura");
  metricCard(
    marginX + (cardW + gap) * 5,
    cardY,
    cardW,
    cardH,
    "Nómina",
    showSalaries ? money(totalNomina) : "OCULTA",
    showSalaries ? "Monto visible" : "Protegida"
  );

  sectionTitle("Lectura ejecutiva", 220);

  autoTable(doc, {
    startY: 238,
    head: [["Indicador", "Resultado"]],
    body: [
      ["Área con mayor personal", `${areaPrincipal[0]} (${areaPrincipal[1]} colaboradores)`],
      ["Oficina con mayor personal", `${oficinaPrincipal[0]} (${oficinaPrincipal[1]} colaboradores)`],
      ["Cobertura territorial", `${porUbicacion.length} ubicación(es) registradas`],
      ["Estructura funcional", `${areas} área(s) operativa(s)`],
      ["Estatus general", `${activos} activo(s), ${inactivos} inactivo(s)`],
    ],
    styles: { font: "helvetica", fontSize: 9, cellPadding: 7 },
    headStyles: { fillColor: [15, 23, 42], textColor: 255, fontStyle: "bold" },
    alternateRowStyles: { fillColor: [248, 250, 252] },
    columnStyles: { 0: { fontStyle: "bold", cellWidth: 210 } },
    margin: { left: marginX, right: marginX },
  });

  sectionTitle("Distribución rápida", doc.lastAutoTable.finalY + 32);

  autoTable(doc, {
    startY: doc.lastAutoTable.finalY + 50,
    head: [["Área", "Personal", "%", "Representación"]],
    body: barTableRows(porArea),
    styles: { font: "helvetica", fontSize: 8.5, cellPadding: 6 },
    headStyles: { fillColor: [30, 64, 175], textColor: 255, fontStyle: "bold" },
    alternateRowStyles: { fillColor: [239, 246, 255] },
    columnStyles: { 1: { halign: "center" }, 2: { halign: "center" }, 3: { textColor: [30, 64, 175] } },
    margin: { left: marginX, right: marginX },
  });

  footer();

  // Página 2
  doc.addPage();
  header("Distribución por Área");

  sectionTitle("Personal por área funcional", 86, "Permite identificar concentración de personal por estructura interna.");

  autoTable(doc, {
    startY: 120,
    head: [["Área", "Personal", "%", "Representación"]],
    body: barTableRows(porArea),
    styles: { font: "helvetica", fontSize: 9, cellPadding: 7 },
    headStyles: { fillColor: [15, 23, 42], textColor: 255, fontStyle: "bold" },
    alternateRowStyles: { fillColor: [248, 250, 252] },
    columnStyles: { 1: { halign: "center" }, 2: { halign: "center" }, 3: { textColor: [79, 70, 229] } },
    margin: { left: marginX, right: marginX },
  });

  footer();

  // Página 3
  doc.addPage();
  header("Personal por Oficina");

  sectionTitle("Cobertura operativa por oficina", 86, "Agrupación por oficina para lectura territorial y operativa.");

  let y = 120;

  porOficina.forEach(([oficina]) => {
    const personas = empleados.filter((e) => (normalize(e.oficina) || "Sin clasificar") === oficina);

    if (y > pageHeight - 120) {
      footer();
      doc.addPage();
      header("Personal por Oficina", `Generado: ${generated}`);
      y = 86;
    }

    doc.setFont("helvetica", "bold");
    doc.setFontSize(11);
    doc.setTextColor(15, 23, 42);
    doc.text(`${oficina} (${personas.length})`, marginX, y);

    autoTable(doc, {
      startY: y + 10,
      head: [["Empleado", "Rol", "Área", "Ubicación", "Teléfono"]],
      body: personas.map((e) => [
        normalize(e.nombre) || "-",
        normalize(e.rol) || "-",
        normalize(e.area) || "-",
        normalize(e.ubicacion) || "-",
        normalize(e.telefono) || "-",
      ]),
      styles: { font: "helvetica", fontSize: 8, cellPadding: 5 },
      headStyles: { fillColor: [51, 65, 85], textColor: 255, fontStyle: "bold" },
      alternateRowStyles: { fillColor: [248, 250, 252] },
      margin: { left: marginX, right: marginX },
    });

    y = doc.lastAutoTable.finalY + 28;
  });

  footer();

  // Página 4
  doc.addPage();
  header("Directorio Detallado");

  sectionTitle(
    "Relación completa de empleados",
    86,
    "Detalle operativo para consulta, validación y seguimiento."
  );

  autoTable(doc, {
    startY: 120,
    head: [["Empleado", "Rol", "Área", "Ubicación", "Oficina", "Teléfono", "Estatus", "Sueldo"]],
    body: empleados.map((e) => [
      normalize(e.nombre) || "-",
      normalize(e.rol) || "-",
      normalize(e.area) || "-",
      normalize(e.ubicacion) || "-",
      normalize(e.oficina) || "-",
      normalize(e.telefono) || "-",
      getEstatus(e),
      showSalaries ? money(e.sueldo) : "OCULTO",
    ]),
    styles: { font: "helvetica", fontSize: 8, cellPadding: 5.5, overflow: "linebreak" },
    headStyles: { fillColor: [15, 23, 42], textColor: 255, fontStyle: "bold" },
    alternateRowStyles: { fillColor: [248, 250, 252] },
    columnStyles: {
      0: { cellWidth: 140 },
      1: { cellWidth: 120 },
      2: { cellWidth: 85 },
      3: { cellWidth: 105 },
      4: { cellWidth: 150 },
      5: { cellWidth: 75 },
      6: { cellWidth: 65 },
      7: { cellWidth: 70 },
    },
    margin: { left: marginX, right: marginX },
  });

  footer();

  const totalPages = doc.internal.getNumberOfPages();
  for (let i = 1; i <= totalPages; i += 1) {
    doc.setPage(i);
    footer();
  }

  doc.save(`directorio_ejecutivo_empleados_${filenameStamp()}.pdf`);
}